import React from "react";
import { ShieldCheck, Headphones, Award, MapPin, HelpCircle, CheckCircle2 } from "lucide-react";

export function TrustModules({ t, category, locale }) {
  // Content based on category and locale
  const content = {
    pt: {
      title: "CONFIANÇA E SEGURANÇA",
      subtitle: "Projetado para tranquilidade",
      warranty: { t: "Garantia SharkX", d: "1 ano de garantia limitada contra defeitos de fabricação. Suporte técnico NO BRASIL especializado para manter você na água." },
      support: { t: "Suporte Global", d: "Assistência técnica em todos os nossos centros parceiros. Peças de reposição genuínas e manutenção certificada. Resposta em até 24h." },
      safety: { t: "Certificação & Segurança", d: "Componentes com certificação IP68, conformidade com normas de segurança elétrica e testes de estresse rigorosos." },
      social: { t: "Comunidade", d: "Mais de 500 riders ao redor do mundo. A maior rede de e-foiling e jetboarding do hemisfério sul. Junte-se a nós." },
      locations: { t: "Onde Operamos", d: "Centros de excelência no Brasil.  Expansão para EUA, Portugal e Argentina em 2026. Quer ser nosso parceiro? Entre em contato conosco." },
      faqTitle: "Perguntas Frequentes",
      faqs: [
        { q: "Quanto tempo leva para aprender?", a: "A maioria dos nossos alunos decola em até 45 minutos com a supervisão de um instrutor." },
        { q: "A bateria é modular?", a: "Sim, o sistema Plug & Surf permite a troca rápida entre e-foils e jetboards." },
        { q: "Qual a manutenção necessária?", a: "Lavagem com água doce após cada uso em água salgada e verificação e limpeza periódica dos contatos eletrônicos aplicando a graxa especial enviada junto com o e-foil ou jetboard." },
        { q: "Como funciona a garantia?", a: "Garantia de 1 ano cobre defeitos de fabricação. Processo simples via nossa página de suporte e garantia." },
        { q: "Posso alugar antes de comprar?", a: "Sim, oferecemos sessões demo e aluguel diário no Brasil." },
      ]
    },
    en: {
      title: "CONFIDENCE & TRUST",
      subtitle: "Engineered for Peace of Mind",
      warranty: { t: "SharkX Warranty", d: "1 year limited warranty against manufacturing defects. Specialized technical support IN BRAZIL to keep you on the water." },
      support: { t: "Global Support", d: "Technical assistance at all our partner centers. Genuine spare parts and certified maintenance. Response within 24 hours." },
      safety: { t: "Certification & Safety", d: "IP68 certified components, compliance with electrical safety standards, and rigorous stress testing." },
      social: { t: "Community", d: "Over 500 riders worldwide. The largest e-foiling and jetboarding network in the southern hemisphere. Join us." },
      locations: { t: "Operating Locations", d: "Centers of excellence in Brazil. Expansion to USA, Portugal, and Argentina in 2026. Want to be our partner? Get in touch." },
      faqTitle: "Frequently Asked Questions",
      faqs: [
        { q: "How long does it take to learn?", a: "Most of our students achieve lift-off within 45 minutes under instructor supervision." },
        { q: "Is the battery modular?", a: "Yes, the Plug & Surf system allows quick swapping between e-foils and jetboards." },
        { q: "What maintenance is required?", a: "Freshwater rinse after each saltwater use and periodic inspection and cleaning of electrical contacts by applying the special grease shipped with your e-foil or jetboard." },
        { q: "How does warranty work?", a: "1-year warranty covers manufacturing defects. Simple process via our support and warranty page." },
        { q: "Can I rent before buying?", a: "Yes, we offer demo sessions and daily rentals in Brazil." },
      ]
    },
    es: {
      title: "CONFIANZA Y SEGURIDAD",
      subtitle: "Diseñado para la tranquilidad",
      warranty: { t: "Garantía SharkX", d: "1 año de garantía limitada contra defectos de fabricación. Soporte técnico EN BRASIL especializado para mantenerte en el agua." },
      support: { t: "Soporte Global", d: "Asistencia técnica en todos nuestros centros asociados. Repuestos genuinos y mantenimiento certificado. Respuesta en 24 horas." },
      safety: { t: "Certificación & Seguridad", d: "Componentes con certificación IP68, cumplimiento de normas de seguridad eléctrica y pruebas de estrés rigurosas." },
      social: { t: "Comunidad", d: "Más de 500 riders en todo el mundo. La red más grande de e-foiling y jetboarding del hemisferio sur. Únete a nosotros." },
      locations: { t: "Ubicaciones de Operación", d: "Centros de excelencia en Brasil. Expansión a EE.UU., Portugal y Argentina en 2026. ¿Quieres ser nuestro socio? Contáctanos." },
      faqTitle: "Preguntas Frecuentes",
      faqs: [
        { q: "¿Cuánto tiempo toma aprender?", a: "La mayoría de nuestros alumnos logran despegar en hasta 45 minutos bajo supervisión de un instructor." },
        { q: "¿La batería es modular?", a: "Sí, el sistema Plug & Surf permite el cambio rápido entre e-foils y jetboards." },
        { q: "¿Qué mantenimiento se requiere?", a: "Enjuague con agua dulce después de cada uso en agua salada e inspección y limpieza periódica de los contactos eléctricos aplicando la grasa especial incluida con el e-foil o jetboard." },
        { q: "¿Cómo funciona la garantía?", a: "La garantía de 1 año cubre defectos de fabricación. Proceso simple a través de nuestra página de soporte y garantía." },
        { q: "¿Puedo alquilar antes de comprar?", a: "Sí, ofrecemos sesiones demo y alquiler diario en Brasil." },
      ]
    }
  };

  const text = content[locale] || content.en;

  return (
    <section className="py-20 px-6 md:px-12 max-w-[1440px] mx-auto bg-slate-50 rounded-3xl my-16" data-testid="trust-layers">
      <div className="text-center mb-16">
        <div className="overline mb-3">{text.title}</div>
        <h2 className="font-serif-display text-4xl md:text-6xl text-slate-900">{text.subtitle}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        <div className="card-clean p-8 flex flex-col gap-4">
          <ShieldCheck className="text-sky-500" size={32} />
          <h3 className="font-serif-display text-2xl text-slate-900">{text.warranty.t}</h3>
          <p className="text-slate-600 leading-relaxed">{text.warranty.d}</p>
        </div>
        <div className="card-clean p-8 flex flex-col gap-4">
          <Headphones className="text-sky-500" size={32} />
          <h3 className="font-serif-display text-2xl text-slate-900">{text.support.t}</h3>
          <p className="text-slate-600 leading-relaxed">{text.support.d}</p>
        </div>
        <div className="card-clean p-8 flex flex-col gap-4">
          <Award className="text-sky-500" size={32} />
          <h3 className="font-serif-display text-2xl text-slate-900">{text.safety.t}</h3>
          <p className="text-slate-600 leading-relaxed">{text.safety.d}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
        <div className="flex gap-6 items-start">
          <div className="p-3 rounded-full bg-sky-100 text-sky-600">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <h4 className="font-serif-display text-2xl text-slate-900">{text.social.t}</h4>
            <p className="text-slate-600 mt-2">{text.social.d}</p>
          </div>
        </div>
        <div className="flex gap-6 items-start">
          <div className="p-3 rounded-full bg-sky-100 text-sky-600">
            <MapPin size={24} />
          </div>
          <div>
            <h4 className="font-serif-display text-2xl text-slate-900">{text.locations.t}</h4>
            <p className="text-slate-600 mt-2">{text.locations.d}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <HelpCircle className="text-sky-500" size={28} />
          <h3 className="font-serif-display text-3xl text-slate-900">{text.faqTitle}</h3>
        </div>
        <div className="space-y-4">
          {text.faqs.map((faq, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200">
              <div className="font-bold text-slate-900 mb-2">{faq.q}</div>
              <div className="text-slate-600 text-sm">{faq.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
